
import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Calculator, Euro } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useWorkoutTypes } from '@/hooks/useWorkoutTypes';
import { ClientSubscription } from '@/models/Client';

interface PaymentSubscriptionSelectorProps {
  subscriptions: ClientSubscription[];
  selectedSubscriptionId: string;
  onSubscriptionSelect: (subscriptionId: string) => void;
}

const PaymentSubscriptionSelector: React.FC<PaymentSubscriptionSelectorProps> = ({
  subscriptions,
  selectedSubscriptionId,
  onSubscriptionSelect,
}) => {
  const { t } = useTranslation();
  const { workoutTypes = [] } = useWorkoutTypes();

  const getWorkoutTypeName = (workoutTypeId: string) => {
    const workoutType = workoutTypes.find(wt => wt.id === workoutTypeId);
    return workoutType?.name || t('clients.unknownWorkoutType');
  };

  return (
    <div className="space-y-3">
      <Label className="greek-text text-base font-medium">
        Επιλέξτε Συνδρομή για Πληρωμή *
      </Label>
      
      <ScrollArea className="h-64 w-full rounded-md border p-4">
        <RadioGroup
          value={selectedSubscriptionId}
          onValueChange={onSubscriptionSelect}
          className="space-y-3"
        >
          {subscriptions && subscriptions.length > 0 ? (
            subscriptions.map((subscription) => {
              const remainingSessions = subscription.sessionsPerMonth - subscription.currentCycleSessionsUsed;
              
              return (
                <div key={subscription.id} className="flex items-start space-x-3 p-3 rounded-md hover:bg-gray-50 dark:hover:bg-gray-800 border">
                  <RadioGroupItem 
                    value={subscription.id} 
                    id={subscription.id}
                    className="mt-1"
                  />
                  <Label 
                    htmlFor={subscription.id} 
                    className="font-normal text-sm cursor-pointer flex-1"
                  >
                    <div className="space-y-2">
                      <div className="font-medium text-gray-900 dark:text-gray-100 greek-text">
                        {getWorkoutTypeName(subscription.workoutTypeId)}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-xs text-gray-600 dark:text-gray-400">
                        <div>
                          <span className="text-muted-foreground">{t('clients.sessionsPerMonth')}:</span>
                          <div className="font-medium">{subscription.sessionsPerMonth}</div>
                        </div>
                        
                        <div>
                          <span className="text-muted-foreground">{t('clients.remaining')}:</span>
                          <div className="font-medium">{remainingSessions}</div>
                        </div>
                        
                        <div className="col-span-2">
                          <span className="text-muted-foreground">{t('clients.monthlyAmount')}:</span>
                          <div className="font-bold text-primary flex items-center gap-1">
                            <Euro className="h-3 w-3" />
                            €{subscription.price.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Label>
                </div>
              );
            })
          ) : (
            <div className="text-sm text-muted-foreground py-8 text-center">
              Δεν βρέθηκαν ενεργές συνδρομές
            </div>
          )}
        </RadioGroup>
      </ScrollArea>
      
      {subscriptions && subscriptions.length > 0 && (
        <div className="text-xs text-muted-foreground">
          Βρέθηκαν {subscriptions.length} ενεργές συνδρομές
        </div>
      )}
    </div>
  );
};

export default PaymentSubscriptionSelector;
